<script lang="ts">
  import { CUR, THREAD, byId, ROMAN, type CurKey } from '../data/grimoire';
  import { store } from '../store.svelte';
  import { reveal } from '../actions';
  import { onMount } from 'svelte';

  let { dimmed = false }: { dimmed?: boolean } = $props();

  // Loom geometry (matches the original).
  const W = 720, H = 440, padX = 78, padTop = 42, padBot = 34;
  const curKeys = Object.keys(CUR) as CurKey[];
  const colX = (k: CurKey) => padX + curKeys.indexOf(k) * ((W - padX * 2) / (curKeys.length - 1));
  const rowY = (i: number) => padTop + i * ((H - padTop - padBot) / 3);
  const REGCOL = ['var(--nigredo)', 'var(--albedo)', 'var(--citrinitas)', 'var(--rubedo)'];

  type Pt = { x: number; y: number; id: string };
  type Thread = {
    cur: CurKey;
    color: string;
    right: boolean;
    pts: Pt[];
  };

  const threads: Thread[] = (Object.entries(THREAD) as Array<[CurKey, (string | null)[]]>).map(
    ([k, ids]) => {
      const c = CUR[k].cc;
      const x = colX(k);
      const ci = curKeys.indexOf(k);
      const right = ci >= curKeys.length / 2;
      const pts = ids
        .map((id, i) => (id ? { x, y: rowY(i), id } : null))
        .filter((p): p is Pt => !!p);
      return { cur: k, color: c, right, pts };
    },
  );

  const cur = $derived(CUR[store.activeCur as CurKey]);
  const thread = $derived(THREAD[store.activeCur as CurKey] || []);

  const pathHtml = $derived(
    thread
      .map((id) => (id ? `<b>${byId[id].nm}</b>` : '<span style="opacity:.4">—</span>'))
      .join(' &nbsp;→&nbsp; '),
  );

  // Mobile path view: render the active thread as a vertical ladder of cells.
  // Each cell = (stage label, colored bar, entry name or em-dash). Tapping a
  // cell opens the entry directly — no need for the dblclick affordance that
  // desktop has on the SVG dots.
  const REG_LADDER = ['reg-1', 'reg-2', 'reg-3', 'reg-4'] as const;
  const REG_NAME: Record<(typeof REG_LADDER)[number], string> = {
    'reg-1': 'Nigredo',
    'reg-2': 'Albedo',
    'reg-3': 'Citrinitas',
    'reg-4': 'Rubedo',
  };

  // Reduced motion: don't change layout based on scroll, just CSS media query.
  // We use JS here only to mirror matchMedia so SSR / no-JS still shows the SVG.
  let isNarrow = $state(false);
  onMount(() => {
    const mq = window.matchMedia('(max-width: 720px)');
    const update = () => (isNarrow = mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  });
</script>

<section class="currents" id="currents" class:dimmed>
  <div class="wrap">
    <div class="currents__head reveal" use:reveal>
      <div class="eyebrow">☿ Меркуриевы струны</div>
      <h2>Что с чем связано</h2>
      <p class="currents__lead">
        Карты — не четыре списка, а семь струн, идущих вертикально сквозь регистры.
        Одна способность входит как доказанное, истончается на границе, поднимается мифом
        и раскрывается за линией. Возьми струну — увидишь её путь.
        Некоторые начинаются не со дна: это правда структуры, а не пробел.
      </p>
    </div>

    <div class="loom reveal" use:reveal>
      <!-- Left rail: list of threads (always visible, both layouts). -->
      <div class="cur-list">
        {#each curKeys as k (k)}
          <button
            class:on={store.activeCur === k}
            style:--cc={CUR[k].cc}
            onclick={() => store.setCur(k)}
            onmouseover={() => store.setCur(k)}
            onfocus={() => store.setCur(k)}
          >
            <span class="g">{CUR[k].g}</span>
            <span>
              <span class="nm">{CUR[k].nm}</span>
              <span class="ds">{CUR[k].ds}</span>
            </span>
          </button>
        {/each}
      </div>

      <div>
        <!-- Desktop: SVG loom (≥721px) -->
        {#if !isNarrow}
          <div class="loomstage">
            <svg viewBox="0 0 {W} {H}" role="img" aria-label="Диаграмма струн сквозь четыре регистра">
              {#each [0, 1, 2, 3] as i (i)}
                <line x1={padX - 34} y1={rowY(i)} x2={W - 26} y2={rowY(i)} stroke="var(--hair-2)" />
                <text class="lm-band" x={padX - 44} y={rowY(i) + 3} text-anchor="end" fill={REGCOL[i]}>
                  {['I', 'II', 'III', 'IV'][i]}
                </text>
              {/each}

              {#each threads as t (t.cur)}
                {#if t.pts.length > 1}
                  <path
                    class="lm-thread"
                    class:on={store.activeCur === t.cur}
                    data-cur={t.cur}
                    d={'M' + t.pts.map((p) => `${p.x},${p.y}`).join(' L')}
                    stroke={t.color}
                  />
                {/if}
                {#each t.pts as p (p.id)}
                  <circle
                    class="lm-dot"
                    class:on={store.activeCur === t.cur}
                    class:off={store.activeCur !== t.cur}
                    data-cur={t.cur}
                    data-entry={p.id}
                    cx={p.x}
                    cy={p.y}
                    r="4.5"
                    fill={t.color}
                    role="button"
                    tabindex="0"
                    aria-label={byId[p.id].nm}
                    onclick={() => store.setCur(t.cur)}
                    ondblclick={() => store.open(p.id)}
                    onmouseover={() => store.setCur(t.cur)}
                    onfocus={() => store.setCur(t.cur)}
                    onkeydown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        if (ev.shiftKey) store.open(p.id);
                        else store.setCur(t.cur);
                      }
                    }}
                  />
                  <text
                    class="lm-lbl"
                    class:on={store.activeCur === t.cur}
                    data-cur={t.cur}
                    x={t.right ? p.x - 9 : p.x + 9}
                    y={p.y + 4}
                    text-anchor={t.right ? 'end' : 'start'}
                  >
                    {byId[p.id].nm}
                  </text>
                {/each}
              {/each}
            </svg>
          </div>
        {:else}
          <!-- Mobile: vertical ladder of the active thread. -->
          <ol class="mlad" aria-label="Струна по регистрам">
            {#each REG_LADDER as reg, i (reg)}
              {@const id = thread[i]}
              {@const entry = id ? byId[id] : null}
              <li class="mlad__row" class:gap={!entry}>
                <span class="mlad__rg" style:color={REGCOL[i]}>
                  <b>{ROMAN[reg]}</b>
                  <i>{REG_NAME[reg]}</i>
                </span>
                <span class="mlad__bar" style:background={cur?.cc} class:empty={!entry}></span>
                {#if entry}
                  <button class="mlad__cell" onclick={() => store.open(entry.id)}>
                    {entry.nm}
                  </button>
                {:else}
                  <span class="mlad__cell mlad__cell--none">— струна не представлена —</span>
                {/if}
              </li>
            {/each}
          </ol>
        {/if}

        <div class="cur-read" style:--cc={cur?.cc}>
          {#if cur}
            {@html `${cur.g} <b>${cur.nm}</b> &nbsp;—&nbsp; ${cur.ds}.<br>${pathHtml}`}
            {#if !isNarrow}
              <div class="cur-hint">коснись дважды точки — открыть узел</div>
            {/if}
          {/if}
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  .currents.dimmed {
    opacity: 0.4;
    transition: opacity 0.3s;
    pointer-events: none;
  }

  /* Mobile vertical thread ladder.
     Shown only when isNarrow (≤720px). Tighter than the desktop SVG,
     scannable, tap-to-open each register's node directly. */
  .mlad {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0;
    border: 1px solid var(--hair-2);
    border-radius: 6px;
    overflow: hidden;
    background: linear-gradient(180deg, rgba(207, 216, 222, 0.02), rgba(11, 10, 15, 0) 30%);
  }
  .mlad__row {
    display: grid;
    grid-template-columns: 56px 4px 1fr;
    align-items: center;
    gap: 12px;
    padding: 11px 14px;
    border-bottom: 1px solid var(--hair-2);
    min-height: 44px;
  }
  .mlad__row:last-child {
    border-bottom: 0;
  }
  .mlad__rg {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.05;
  }
  .mlad__rg b {
    font-family: var(--display);
    font-weight: 500;
    font-size: 1.2rem;
  }
  .mlad__rg i {
    font-family: var(--mono);
    font-style: normal;
    font-size: 0.62rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-faint);
    margin-top: 1px;
  }
  .mlad__bar {
    height: 26px;
    border-radius: 2px;
    opacity: 0.85;
    align-self: stretch;
    margin-block: 4px;
  }
  .mlad__bar.empty {
    opacity: 0.18;
    background: var(--ink-faint) !important;
  }
  .mlad__cell {
    background: none;
    border: 0;
    color: var(--ink);
    font-family: var(--display);
    font-size: 1.08rem;
    text-align: left;
    cursor: pointer;
    padding: 4px 0;
    line-height: 1.2;
    transition: color 0.2s;
  }
  .mlad__cell:hover {
    color: var(--mercury);
  }
  .mlad__cell--none {
    color: var(--ink-faint);
    font-family: var(--serif);
    font-style: italic;
    font-size: 0.86rem;
    cursor: default;
  }
</style>
